import React from 'react';
import {
  Box,
  Button,
  Chip,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  BarChartRounded as VolumeDryUpIcon,
  BoltRounded as BoltIcon,
  CandlestickChart as CandlestickIcon,
  InsightsRounded as SupportZoneIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
  LocalFireDepartment as FireIcon,
  ShowChartRounded as BreakoutSetupIcon,
  TimelineRounded as CoilStructureIcon,
  TrendingFlat as TrendingFlatIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const SIGNAL_META = {
  vol_exhaustion: { key: 'vol_exhaustion', Icon: VolumeDryUpIcon, color: '#6366f1' },
  support_hold: { key: 'support_hold', Icon: SupportZoneIcon, color: '#0ea5e9' },
  volatility_coil: { key: 'volatility_coil', Icon: CoilStructureIcon, color: '#8b5cf6' },
  close_strength: { key: 'close_strength', Icon: CandlestickIcon, color: '#10b981' },
  slope_turning: { key: 'slope_turning', Icon: TrendingUpIcon, color: '#f59e0b' },
  score_momentum: { key: 'score_momentum', Icon: FireIcon, color: '#ef4444' },
  ma_breakout_setup: { key: 'ma_breakout_setup', Icon: BreakoutSetupIcon, color: '#3b82f6' },
};

function PreMarkupRow({ item, isLast, onClick }) {
  const { t } = useTranslation();
  const readinessPct = Math.round(item.readiness * 100);
  const isHot = item.readiness >= 0.65;
  const isWarm = item.readiness >= 0.5;
  const sectorLabel = item.sector_name || item.sector_code || '—';

  const barGradient = isHot
    ? 'linear-gradient(90deg, #f59e0b, #ef4444)'
    : isWarm
      ? 'linear-gradient(90deg, #fbbf24, #f59e0b)'
      : 'linear-gradient(90deg, #cbd5e1, #94a3b8)';

  const pctColor = isHot ? '#dc2626' : isWarm ? '#d97706' : '#64748b';
  const topSignals = Object.entries(item.signals || {})
    .sort(([, first], [, second]) => second - first)
    .slice(0, 4)
    .filter(([, value]) => value > 0.3);
  const phaseLabel = t(`phase.phases.${item.phase}`);
  const leadSignal = topSignals[0] || null;
  const leadMeta = leadSignal
    ? (SIGNAL_META[leadSignal[0]] || { key: leadSignal[0], Icon: TrendingFlatIcon, color: '#94a3b8' })
    : isHot
      ? { key: 'score_momentum', Icon: BoltIcon, color: '#ef4444' }
      : isWarm
        ? { key: 'slope_turning', Icon: TrendingUpIcon, color: '#f59e0b' }
        : { key: 'support_hold', Icon: SupportZoneIcon, color: '#94a3b8' };
  const leadLabel = leadSignal
    ? t(`phase.preMarkup.signals.${leadMeta.key}`)
    : phaseLabel;
  const LeadIcon = leadMeta.Icon;
  const leadIconBg = `${leadMeta.color}12`;
  const leadIconBorder = `${leadMeta.color}30`;
  const leadTooltip = leadSignal
    ? `${leadLabel}: ${Math.round(leadSignal[1] * 100)}%`
    : leadLabel;

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: { xs: 'flex-start', md: 'center' },
        gap: { xs: 1, sm: 1.6 },
        px: { xs: 1.35, sm: 2.2 },
        py: 1.4,
        mx: 0,
        borderRadius: 0,
        cursor: 'pointer',
        transition: 'all 0.15s',
        borderBottom: isLast ? 'none' : '1px solid #f1f5f9',
        flexWrap: { xs: 'wrap', md: 'nowrap' },
        '&:hover': {
          backgroundColor: '#fffcf5',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, width: { xs: '100%', md: 290 }, minWidth: 0, flexShrink: 0 }}>
        <Tooltip title={leadTooltip} arrow>
          <Box sx={{
            width: 32,
            height: 32,
            borderRadius: '8px',
            backgroundColor: leadIconBg,
            border: `1px solid ${leadIconBorder}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <LeadIcon sx={{ fontSize: 16, color: leadMeta.color }} />
          </Box>
        </Tooltip>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{
            fontSize: '0.82rem',
            fontWeight: 800,
            color: '#1e293b',
            fontFamily: 'monospace',
            lineHeight: 1.2,
          }}>
            {item.ticker}
          </Typography>
          <Typography sx={{
            fontSize: '0.62rem',
            color: '#94a3b8',
            fontFamily: 'monospace',
          }}>
            {item.close?.toLocaleString()}
          </Typography>
          {item.company_name ? (
            <Typography sx={{
              mt: 0.32,
              maxWidth: { xs: 140, sm: 220 },
              fontSize: '0.64rem',
              fontWeight: 600,
              color: '#475569',
              lineHeight: 1.35,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              {item.company_name}
            </Typography>
          ) : null}
        </Box>
      </Box>

      <Box sx={{
        width: { xs: '100%', md: 132 },
        minWidth: 0,
        flexShrink: 0,
        display: 'flex',
        alignItems: { xs: 'flex-start', md: 'center' },
      }}>
        <Typography sx={{
          fontSize: '0.66rem',
          fontWeight: 700,
          color: sectorLabel === '—' ? '#cbd5e1' : '#b45309',
          lineHeight: 1.35,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
          {sectorLabel}
        </Typography>
      </Box>

      <Box sx={{ width: { xs: 90, sm: 110 }, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 0.4 }}>
          <Typography sx={{
            fontSize: '0.85rem',
            fontWeight: 800,
            fontFamily: 'monospace',
            color: pctColor,
            lineHeight: 1,
          }}>
            {readinessPct}%
          </Typography>
          <Typography sx={{
            fontSize: '0.58rem',
            fontWeight: 600,
            color: '#94a3b8',
          }}>
            {t('phase.preMarkup.readyLabel')}
          </Typography>
        </Box>
        <Box sx={{
          width: '100%',
          height: 5,
          borderRadius: 3,
          backgroundColor: '#f1f5f9',
          overflow: 'hidden',
        }}>
          <Box sx={{
            width: `${readinessPct}%`,
            height: '100%',
            borderRadius: 3,
            background: barGradient,
            transition: 'width 0.4s ease',
          }} />
        </Box>
      </Box>

      <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.75, flex: 1, minWidth: 0, flexWrap: 'wrap' }}>
        {topSignals.map(([key, value]) => {
          const meta = SIGNAL_META[key] || { key, Icon: TrendingFlatIcon, color: '#94a3b8' };
          const signalLabel = t(`phase.preMarkup.signals.${meta.key}`);
          const SignalIcon = meta.Icon;
          const isStrong = value >= 0.7;

          return (
            <Tooltip key={key} title={`${signalLabel}: ${Math.round(value * 100)}%`} arrow>
              <Chip
                icon={<SignalIcon sx={{ fontSize: 13 }} />}
                label={signalLabel}
                size="small"
                sx={{
                  height: 24,
                  fontSize: '0.62rem',
                  fontWeight: 600,
                  backgroundColor: isStrong ? `${meta.color}12` : '#f8fafc',
                  color: isStrong ? meta.color : '#64748b',
                  border: `1px solid ${isStrong ? `${meta.color}30` : '#e2e8f0'}`,
                  borderRadius: '7px',
                  '& .MuiChip-icon': {
                    color: isStrong ? meta.color : '#94a3b8',
                    ml: 0.5,
                  },
                  '& .MuiChip-label': { px: 0.6, pr: 1 },
                }}
              />
            </Tooltip>
          );
        })}
        {topSignals.length === 0 && (
          <Typography sx={{ fontSize: '0.65rem', color: '#cbd5e1', fontStyle: 'italic' }}>
            {t('phase.preMarkup.weakSignals')}
          </Typography>
        )}
      </Box>

      <Box sx={{ flexShrink: 0 }}>
        <Chip
          label={phaseLabel}
          size="small"
          sx={{
            height: 24,
            fontSize: '0.65rem',
            fontWeight: 700,
            backgroundColor: item.phase === 'accumulation' ? '#dbeafe' : '#f3e8ff',
            color: item.phase === 'accumulation' ? '#1e40af' : '#7c3aed',
            border: `1px solid ${item.phase === 'accumulation' ? '#93c5fd' : '#c4b5fd'}`,
            borderRadius: '7px',
          }}
        />
      </Box>
    </Box>
  );
}

export default function PreMarkupSection({
  candidates,
  isLoading,
  isExpanded,
  onToggleExpanded,
  onTickerSelect,
}) {
  const { t } = useTranslation();

  if (isLoading && !candidates) {
    return (
      <Box sx={{
        mt: 3,
        background: '#fff',
        borderRadius: '14px',
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
      }}>
        <Box sx={{
          height: 3,
          background: 'linear-gradient(90deg, #f59e0b 0%, #ef4444 30%, #f59e0b 60%, #ef4444 100%)',
          backgroundSize: '200% 100%',
          animation: 'shimmerBar 1.8s ease-in-out infinite',
          '@keyframes shimmerBar': {
            '0%': { backgroundPosition: '200% 0' },
            '100%': { backgroundPosition: '-200% 0' },
          },
        }} />

        <Box sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
            <Box sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(245,158,11,0.3)',
              animation: 'pulseGlow 2s ease-in-out infinite',
              '@keyframes pulseGlow': {
                '0%, 100%': { boxShadow: '0 2px 8px rgba(245,158,11,0.3)' },
                '50%': { boxShadow: '0 4px 20px rgba(245,158,11,0.5)' },
              },
            }}>
              <BoltIcon sx={{
                color: '#fff',
                fontSize: 22,
                animation: 'boltFlash 1.5s ease-in-out infinite',
                '@keyframes boltFlash': {
                  '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                  '50%': { opacity: 0.6, transform: 'scale(0.9)' },
                },
              }} />
            </Box>
            <Box>
              <Typography sx={{
                fontSize: '0.88rem',
                fontWeight: 800,
                color: '#92400e',
                lineHeight: 1.2,
              }}>
                {t('phase.preMarkup.scanning')}
              </Typography>
              <Typography sx={{
                fontSize: '0.68rem',
                color: '#b45309',
                mt: 0.3,
                fontWeight: 600,
              }}>
                {t('phase.preMarkup.scanningDetail')}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {[0.9, 0.7, 0.45].map((opacity, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  px: 2,
                  py: 1.2,
                  borderRadius: '10px',
                  backgroundColor: '#fefce8',
                  opacity,
                  animation: `fadeSlide 1.6s ease-in-out ${idx * 0.25}s infinite`,
                  '@keyframes fadeSlide': {
                    '0%': { opacity: opacity * 0.3, transform: 'translateX(-4px)' },
                    '40%, 60%': { opacity, transform: 'translateX(0)' },
                    '100%': { opacity: opacity * 0.3, transform: 'translateX(4px)' },
                  },
                }}
              >
                <Box sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '8px',
                  background: 'linear-gradient(90deg, #fde68a, #fcd34d, #fde68a)',
                  backgroundSize: '200% 100%',
                  animation: 'shimmer 1.4s ease-in-out infinite',
                  '@keyframes shimmer': {
                    '0%': { backgroundPosition: '200% 0' },
                    '100%': { backgroundPosition: '-200% 0' },
                  },
                }} />
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{
                    width: [65, 80, 55][idx],
                    height: 10,
                    borderRadius: 5,
                    background: 'linear-gradient(90deg, #fde68a, #fcd34d, #fde68a)',
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 1.4s ease-in-out infinite',
                  }} />
                  <Box sx={{
                    width: [110, 90, 70][idx],
                    height: 6,
                    borderRadius: 3,
                    background: 'linear-gradient(90deg, #fef3c7, #fde68a, #fef3c7)',
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 1.4s ease-in-out infinite',
                  }} />
                </Box>
                <Box sx={{
                  width: 80,
                  height: 5,
                  borderRadius: 3,
                  background: 'linear-gradient(90deg, #fde68a, #fdba74, #fde68a)',
                  backgroundSize: '200% 100%',
                  animation: 'shimmer 1.4s ease-in-out infinite',
                }} />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  }

  const isEmpty = !candidates || !candidates.candidates || candidates.candidates.length === 0;
  const topCandidates = isEmpty ? [] : (isExpanded ? candidates.candidates : candidates.candidates.slice(0, 7));
  const hasMore = !isEmpty && candidates.candidates.length > 7;

  return (
    <Box sx={{
      mt: 3,
      background: '#fff',
      borderRadius: '14px',
      border: '1px solid #e2e8f0',
      overflow: 'hidden',
    }}>
      <Box sx={{
        px: { xs: 1.5, sm: 2.5 },
        py: 1.8,
        background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
        borderBottom: '1px solid #fcd34d40',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(245,158,11,0.3)',
          }}>
            <BoltIcon sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{
              fontSize: '0.92rem',
              fontWeight: 800,
              color: '#92400e',
              lineHeight: 1.2,
            }}>
              {t('phase.preMarkup.title')}
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: '#b45309', mt: 0.2 }}>
              {t('phase.preMarkup.subtitle', { total: candidates?.total || 0 })}
            </Typography>
          </Box>
        </Box>
        <Chip
          icon={<TrendingUpIcon sx={{ fontSize: 14 }} />}
          label={t('phase.preMarkup.candidates', { total: candidates?.total || 0 })}
          size="small"
          sx={{
            height: 26,
            fontSize: '0.72rem',
            fontWeight: 700,
            backgroundColor: '#fef3c7',
            color: '#92400e',
            border: '1px solid #fcd34d',
            '& .MuiChip-icon': { color: '#f59e0b' },
          }}
        />
      </Box>

      <Box sx={{ px: 0, py: 0.25 }}>
        {!isEmpty && (
          <Box sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            gap: 1.6,
            px: 2.2,
            pb: 0.75,
            pt: 0.2,
            mx: 0,
          }}>
            <Typography sx={{ width: 290, flexShrink: 0, fontSize: '0.62rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Mã / Công ty
            </Typography>
            <Typography sx={{ width: 132, flexShrink: 0, fontSize: '0.62rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Ngành
            </Typography>
            <Typography sx={{ width: 110, flexShrink: 0, fontSize: '0.62rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Sẵn sàng
            </Typography>
            <Typography sx={{ flex: 1, fontSize: '0.62rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Tín hiệu nổi bật
            </Typography>
            <Typography sx={{ width: 74, flexShrink: 0, fontSize: '0.62rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'right' }}>
              Pha
            </Typography>
          </Box>
        )}
        {isEmpty ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Box sx={{
              width: 48,
              height: 48,
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #fde68a 0%, #fef3c7 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 1.5,
            }}>
              <BoltIcon sx={{ color: '#d97706', fontSize: 24 }} />
            </Box>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#92400e', mb: 0.5 }}>
              {t('phase.preMarkup.noCandidates', 'Chua co co phieu san sang tang toc')}
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: '#b45309' }}>
              {t('phase.preMarkup.noCandidatesDetail', 'He thong chua phat hien co phieu nao co tin hieu san sang chuyen sang giai doan Markup')}
            </Typography>
          </Box>
        ) : topCandidates.map((item, idx) => (
          <PreMarkupRow
            key={item.ticker}
            item={item}
            isLast={idx === topCandidates.length - 1}
            onClick={() => onTickerSelect(item.ticker)}
          />
        ))}
      </Box>

      {hasMore && (
        <Box sx={{
          px: 2.5,
          py: 1.2,
          borderTop: '1px solid #f1f5f9',
          textAlign: 'center',
        }}>
          <Button
            size="small"
            onClick={onToggleExpanded}
            endIcon={isExpanded ? <ArrowUpIcon sx={{ fontSize: 16 }} /> : <ArrowDownIcon sx={{ fontSize: 16 }} />}
            sx={{
              fontSize: '0.72rem',
              fontWeight: 700,
              color: '#b45309',
              textTransform: 'none',
              '&:hover': { backgroundColor: '#fffbeb' },
            }}
          >
            {isExpanded ? t('phase.preMarkup.collapse') : t('phase.preMarkup.showAll', { total: candidates.total })}
          </Button>
        </Box>
      )}
    </Box>
  );
}